// Must match the .custom-product-gallery__item margin-right in
// component-custom-product-gallery.css, since dragging/snapping is
// computed in pixels to account for the gap between slides.
const ITEM_GAP = 12;

class CustomProductGallery extends HTMLElement {
  connectedCallback() {
    this.items = [...this.querySelectorAll('.custom-product-gallery__item')].map((item) => ({
      el: item,
      variantIds: item.dataset.variantIds
        ? item.dataset.variantIds.split(',').filter(Boolean)
        : [],
      colorValue: item.dataset.colorValue || '',
    }));
    this.track = this.querySelector('.custom-product-gallery__track');
    this.viewport = this.querySelector('.custom-product-gallery__viewport');
    this.prevButton = this.querySelector('.custom-product-gallery__arrow--prev');
    this.nextButton = this.querySelector('.custom-product-gallery__arrow--next');
    this.counter = this.querySelector('.custom-product-gallery__counter');
    this.counterCurrent = this.querySelector('.custom-product-gallery__counter-current');
    this.counterTotal = this.querySelector('.custom-product-gallery__counter-total');
    this.sectionId = this.dataset.sectionId;
    this.index = 0;

    if (this.items.length === 0) return;

    this.filterForVariant(this.dataset.initialVariantId, {
      keepCurrentIfVisible: false,
      colorValue: this.dataset.initialColor,
    });

    if (this.track) {
      requestAnimationFrame(() => {
        this.track.classList.add('custom-product-gallery__track--animate');
      });
    }

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.show(this.index - 1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.show(this.index + 1));
    }

    if (this.viewport) {
      this.viewport.addEventListener('touchstart', (event) => this.onTouchStart(event), {
        passive: true,
      });
      this.viewport.addEventListener('touchmove', (event) => this.onTouchMove(event), {
        passive: false,
      });
      this.viewport.addEventListener('touchend', (event) => this.onTouchEnd(event), {
        passive: true,
      });
      this.viewport.addEventListener('touchcancel', (event) => this.onTouchEnd(event), {
        passive: true,
      });
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        this.onVariantChange(event);
      });
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  onVariantChange(event) {
    if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
    const variant = event.data.variant;
    if (!variant) return;

    this.filterForVariant(variant.id, {
      featuredMediaId: variant.featured_media && variant.featured_media.id,
      colorValue: variant.option1,
    });
  }

  // A photo explicitly attached to variants in Admin only shows for those
  // variants' color. Otherwise, if the photo's alt text was matched to a
  // color option value at render time (see custom-product-gallery.liquid),
  // it only shows for that color. Photos with neither are treated as
  // shared and stay visible for every color.
  filterForVariant(variantId, { featuredMediaId, keepCurrentIfVisible = true, colorValue } = {}) {
    const variantIdStr = String(variantId);
    let visible = this.items.filter((item) => {
      if (item.variantIds.length > 0) return item.variantIds.includes(variantIdStr);
      if (item.colorValue) return item.colorValue === colorValue;
      return true;
    });

    if (visible.length === 0) {
      visible = this.items;
    }

    this.visibleItems = visible;

    this.items.forEach((item) => {
      item.el.hidden = !visible.includes(item);
    });

    let targetIndex = 0;
    if (featuredMediaId) {
      const featuredIndex = visible.findIndex(
        (item) => item.el.dataset.mediaId === String(featuredMediaId)
      );
      if (featuredIndex !== -1) targetIndex = featuredIndex;
    } else if (keepCurrentIfVisible && visible[this.index]) {
      targetIndex = this.index;
    }

    this.show(targetIndex);

    const multipleVisible = visible.length > 1;
    if (this.prevButton) this.prevButton.hidden = !multipleVisible;
    if (this.nextButton) this.nextButton.hidden = !multipleVisible;
    if (this.counter) this.counter.hidden = !multipleVisible;
    if (this.counterTotal) this.counterTotal.textContent = visible.length;
  }

  onTouchStart(event) {
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.isDragging = false;
    this.dragBaseOffset = -this.index * (this.viewport.clientWidth + ITEM_GAP);
  }

  onTouchMove(event) {
    if (this.touchStartX === undefined) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (!this.isDragging) {
      // Wait until the gesture is clearly horizontal before hijacking it,
      // so a vertical swipe still scrolls the page normally.
      if (Math.abs(deltaX) < 10 && Math.abs(deltaY) < 10) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        this.touchStartX = undefined;
        return;
      }
      this.isDragging = true;
      if (this.track) {
        this.track.classList.remove('custom-product-gallery__track--animate');
      }
    }

    event.preventDefault();

    if (this.track) {
      this.track.style.transform = `translateX(${this.dragBaseOffset + deltaX}px)`;
    }
  }

  onTouchEnd(event) {
    if (this.touchStartX === undefined) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - this.touchStartX;
    const wasDragging = this.isDragging;
    this.touchStartX = undefined;
    this.touchStartY = undefined;
    this.isDragging = false;

    if (this.track) {
      this.track.classList.add('custom-product-gallery__track--animate');
    }

    if (!wasDragging) return;

    const SWIPE_THRESHOLD = this.viewport.clientWidth * 0.2;
    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      this.show(deltaX < 0 ? this.index + 1 : this.index - 1);
    } else {
      this.show(this.index);
    }
  }

  show(newIndex) {
    const visible = this.visibleItems || this.items;
    const total = visible.length;
    if (total === 0) return;
    this.index = (newIndex + total) % total;

    if (this.track && this.viewport) {
      const step = this.viewport.clientWidth + ITEM_GAP;
      this.track.style.transform = `translateX(-${this.index * step}px)`;
    }

    if (this.counterCurrent) {
      this.counterCurrent.textContent = this.index + 1;
    }
  }
}

customElements.define('custom-product-gallery', CustomProductGallery);
